import { DragEvent, RefObject } from 'react';

import { getDirectionOfMovingItem } from '../../../functions';
import { DirectionDragItemType, ItemType, MappingItemIdIndexType } from '../../../types';
import { findLastExpandedChild } from '../projectTreeUtil';
import { DragStrategy } from './dragStrategy';

export class DragStrategyBetween extends DragStrategy {
    constructor(
        rowsMap: Map<string, RefObject<HTMLDivElement>[]>,
        protected readonly items: ItemType[],
        protected readonly mappingItemIdIndex: MappingItemIdIndexType,
    ) {
        super(rowsMap);
    }

    private availableDropsAbove = new Set<string>();
    private availableDropsBellow = new Set<string>();

    onDragOver(e: DragEvent<HTMLDivElement>, access?: boolean): boolean {
        if (!super.onDragOver(e, access)) {
            return false;
        }

        const [isAllowed, direction] = this.getIsDragOverAllowedDirection(e);

        if (!isAllowed || direction === 'none') {
            this.dragOverItemId = '';
            return false;
        }

        const itemId = DragStrategy.getItemId(e);

        this.setRowClass(
            itemId,
            direction === 'up' ? DragStrategy.CSS_CLASS.dragOverUp : DragStrategy.CSS_CLASS.dragOverDown,
        );

        return true;
    }

    onDragLeave(): void {
        if (this.dragOverItemId) {
            this.removeRowClass(this.dragOverItemId, DragStrategy.CSS_CLASS.dragOverUp);
            this.removeRowClass(this.dragOverItemId, DragStrategy.CSS_CLASS.dragOverDown);
        }
    }

    onDragStart(e: DragEvent<HTMLDivElement>): boolean {
        if (super.onDragStart(e)) {
            this.availableDropsAbove.clear();
            this.availableDropsBellow.clear();

            const item = this.getItem(DragStrategy.getItemId(e));
            if (item) {
                this.collectAvailableDropItemsForItem(item);
            }

            return true;
        }

        return false;
    }

    onDragEnd(e: DragEvent<HTMLDivElement>): string | null {
        const dropItemId = super.onDragEnd(e);
        this.removeRowClass(this.dragOverItemId, DragStrategy.CSS_CLASS.dragOverUp);
        this.removeRowClass(this.dragOverItemId, DragStrategy.CSS_CLASS.dragOverDown);
        return dropItemId;
    }

    private getIsDragOverAllowedDirection = (e: DragEvent<HTMLDivElement>): [boolean, DirectionDragItemType] => {
        const itemVerticalDraggable = this.getItem(this.itemBeingDraggedId);
        const item = this.getItem(DragStrategy.getItemId(e));

        if (!item || !itemVerticalDraggable || item.id === itemVerticalDraggable.id) {
            return [false, 'none'];
        }

        const directionOfMovingItem = getDirectionOfMovingItem(itemVerticalDraggable, item);

        switch (directionOfMovingItem) {
            case 'up':
                return [this.availableDropsAbove.has(item.id), directionOfMovingItem];
            case 'down':
                return [this.availableDropsBellow.has(item.id), directionOfMovingItem];
            default:
                return [false, 'none'];
        }
    };

    private getItem(id: string): ItemType | undefined {
        return this.items[this.mappingItemIdIndex[id]];
    }

    private collectAvailableDropItemsForItem(item: ItemType): void {
        if (item.parentId) {
            const parentItem = this.getItem(item.parentId);

            if (parentItem) {
                this.collectDropItemsAbove(item, parentItem.children);
                this.collectDropItemsBellow(item, parentItem.children);
            }
        }
    }

    private collectDropItemsAbove(item: ItemType, siblings?: string[]): void {
        if (!siblings) {
            return;
        }

        for (const sibling of siblings) {
            if (sibling !== item.id) {
                this.availableDropsAbove.add(sibling);
            }
        }
    }

    private collectDropItemsBellow(item: ItemType, siblings?: string[]): void {
        if (!siblings) {
            return;
        }

        for (let i = siblings.length - 1; i >= 0; --i) {
            const sibling = siblings[i];
            if (sibling === item.id) {
                break;
            }

            this.availableDropsBellow.add(this.findLastExpandedChild(sibling));
        }
    }

    private findLastExpandedChild(itemId: string): string {
        return findLastExpandedChild(itemId, this.items, this.mappingItemIdIndex);
    }
}
